import { useSelector } from 'react-redux';
import { useContext, useEffect } from 'react';
import { UserService } from '../../../services';
import UserRolePopover from "./components/UserRolePopover";
import { ProjectSourcesContext } from '../../../context/ProjectSources';
import { getApplication } from '../../../services/BaseService';

export const USER_STATUS = {
  ACTIVE: {
    value: 'active',
    label: 'Active',
  },
  pending: {
    value: 'pending',
    label: 'Pending',
  },
};

export const useUserRoles = () => {
  const {
    userRoles, setUserRoles, userRolesFetched, setUserRolesFetched,
  } = useContext(ProjectSourcesContext);
  const { user } = useSelector((state) => state.authentication);

  useEffect(() => {
    if (!userRolesFetched && user) {
      const _fetch = async () => {
        try {
          const { data } = await UserService.getRoles();

          if (data.data) {
            setUserRoles(data.data.map((d) => ({
              ...d,
              value: d._id,
              label: d.description,
              canBeAssignedToSubUsers: d.canBeAssignedToSubUsers,
              isAdmin: d.id === 'ROLE_ADMIN', // TO-DO CHANGE THIS
            })));
            setUserRolesFetched(true);
          }
        } catch (err) {
          console.log(err);
        }
      };

      _fetch();
    }
  }, [userRolesFetched, user]);

  return userRoles;
}

export const getFiltersConfig = ({ t, userRoles = [] }) => {
  const app = getApplication();
  return [
    {
      key: 'email',
      label: 'Email',
      optionsName: 'email',
      placeholder: `${t('Select')} ${t('Email')}`,
      type: 'textInput',
    },
    {
      items: [
        {
          value: USER_STATUS.ACTIVE.value,
          label: USER_STATUS.ACTIVE.label,
        },
        {
          value: USER_STATUS.pending.value,
          label: USER_STATUS.pending.label,
        },
      ],
      key: 'status',
      label: 'Status',
      optionsName: 'status',
      placeholder: `${t('Select')} ${t('Status')}`,
      type: 'select',
    },
    {
      key: 'lastLogin',
      label: 'Last Login',
      optionsName: 'lastLogin',
      placeholder: `${t('Select')} ${t('Last Login')}`,
      type: 'timeframe',
    },
    {
      items: userRoles.filter((s) => s.canBeAssignedToSubUsers && s.app === app).map((r) => ({ ...r, label: t(r.label) })),
      key: 'userRole',
      label: 'User Role',
      optionsName: 'userRole',
      placeholder: `${t('Select')} ${t('User Role')}`,
      type: 'select',
    },
  ];
};

export const getUserForm = ({ t, userRoles = [] }) => {
  const app = getApplication();
  return {
    "user": {
      "position": 0,
      "firstName": {
        "group": "user",
        "section": "user",
        "moduleScope": {
          "cadd": [
            "scoping",
            "new",
            "linking-extra"
          ],
          "pme": [
            "scoping",
            "new",
            "linking-extra"
          ]
        },
        "label": t("First Name"),
        "comment": false,
        "inputType": "text",
        "rules": [
          {
            "required": true,
            "message": "This field is required"
          }
        ],
        "mergeStrategy": "simple",
        "position": 1
      },
      "lastName": {
        "group": "user",
        "section": "user",
        "moduleScope": {
          "cadd": [
            "scoping",
            "new",
            "linking-extra"
          ],
          "pme": [
            "scoping",
            "new",
            "linking-extra"
          ]
        },
        "label": t("Last Name"),
        "comment": false,
        "inputType": "text",
        "rules": [
          {
            "required": true,
            "message": "This field is required"
          }
        ],
        "mergeStrategy": "simple",
        "position": 2
      },
      "email": {
        "group": "user",
        "section": "user",
        "moduleScope": {
          "cadd": [
            "scoping",
            "new",
            "linking-extra"
          ],
          "pme": [
            "scoping",
            "new",
            "linking-extra"
          ]
        },
        "label": t("Email"),
        "comment": false,
        "inputType": "text",
        "rules": [
          {
            "required": true,
            "message": "This field is required"
          },
          {
            type: 'email',
            message: 'errors::field must be email'
          }
        ],
        "mergeStrategy": "simple",
        "position": 3
      },
      "role": {
        "group": "user",
        "section": "user",
        "moduleScope": {
          "cadd": [
            "scoping",
            "new",
            "linking",
            "linking-extra",
            "admin"
          ],
          "pme": [
            "scoping",
            "new",
            "linking",
            "linking-extra",
            "admin"
          ]
        },
        "label": t('User Role'),
        "placeholder": t('User Role'),
        "comment": false,
        "inputType": "select",
        "options": userRoles.filter((r) => r.canBeAssignedToSubUsers && r.app === app).map((v) => ({
          label: t(v.label),
          value: v.value,
        })),
        "optionsName": "userRoles",
        tooltip: <UserRolePopover t={t} />,
        "sortOptions": true,
        "rules": [
          {
            "required": true,
            "message": "This field is required"
          }
        ],
        "position": 4
      },
    }
  }
}
